import { useTranslation } from 'react-i18next';
import {
  useDeleteExerciseOption,
  useGetExerciseOptions,
} from '../../../services/redux/api/exerciseConfigAPI';
import { useMemo, useState } from 'react';
import { useAccess } from '../../../hooks/useAccess';
import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import { IExerciseOption } from '../../../types/interfaces/exercise-option';
import { getCellReducers } from './helpers/getCellReducers';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import { FormikProps, useFormik } from 'formik';
import { ExerciseOptionType } from '../../../shared/constants';

export function useExerciseOptionComponent(classes) {
  const { t } = useTranslation();
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [itemForDelete, setItemForDelete] = useState<IExerciseOption>(null);
  const [itemForEditing, setItemForEditing] = useState<IExerciseOption>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteExerciseOption] = useDeleteExerciseOption();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: allOption,
      default: allOption,
      active: allOption,
    },
    onSubmit: () => {},
  });

  const filteredValues = useMemo(
    () =>
      exerciseOptions?.filter((property: IExerciseOption) => {
        const typeFilterId = filterFormik.values.type?.id ?? 'all';
        const defaultFilterId = filterFormik.values.default?.id ?? 'all';
        const activeFilterId = filterFormik.values.active?.id ?? 'all';
        const filterValue = filter.toLowerCase();

        if (
          !String(property.name ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(property.nameKey ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(property.type ?? '')
            .toLowerCase()
            .includes(filterValue)
        ) {
          return false;
        }

        if (typeFilterId !== 'all' && property.type !== typeFilterId)
          return false;

        if (defaultFilterId !== 'all') {
          const isDefault = !!property.default;
          if (
            (defaultFilterId === true && !isDefault) ||
            (defaultFilterId === false && isDefault)
          ) {
            return false;
          }
        }

        if (activeFilterId !== 'all') {
          const isActive = !!property.active;
          if (
            (activeFilterId === true && !isActive) ||
            (activeFilterId === false && isActive)
          ) {
            return false;
          }
        }

        return true;
      }),
    [exerciseOptions, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.exerciseOptions?.canEdit ?? false,
      canDelete: access?.exerciseOptions?.canDelete ?? false,
    },
    (property: IExerciseOption) => {
      setItemForEditing(property);
      setShowEditModal(true);
    },
    (property: IExerciseOption) => {
      setItemForDelete(property);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.EXERCISE_PROPERTY_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteExerciseOption({ id: itemForDelete.id });
  }

  const filterTypeOptions = [
    allOption,
    ...Object.values(ExerciseOptionType).map((type) => ({
      id: type,
      name: type,
    })),
  ];

  const filterDefaultOptions = [
    allOption,
    { id: true, name: t('common.yes') },
    { id: false, name: t('common.no') },
  ];

  const filterActiveOptions = [
    allOption,
    { id: true, name: t('common.active') },
    { id: false, name: t('common.notActive') },
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterTypeOptions,
    filterDefaultOptions,
    filterActiveOptions,
  };
}
